import { RoutePoint } from '../search/search.service';
import { TravelMedia } from '@ridango/core/lib/ridango.models';
import { RequiredParameterType } from '../medium/medium.model';

export enum AreaCode {
    cityOrMunicipality = 'CITY_OR_MUNICIPALITY',
    area = 'AREA',
    aToB = 'A_TO_B'
}

export enum MediumCode {
    email = 'EMAIL',
    personal_code = 'PERSONAL_CODE',
    card = 'CARD'
}

export enum ProductAreaType {
    ONE_AREA = 'ONE_AREA',
    TWO_AREAS = 'TWO_AREAS',
    COUNTY = 'COUNTY'
}

export enum ActivationType {
    AFTER_PURCHASE = 'PURCHASE',
    BEFORE_PURCHASE = 'SPECIFIED_ACTIVATION_DATE',
    MANUAL = 'MANUAL',
    START_OF_PERIOD = 'START_OF_PERIOD'
}

export enum CartStatus {
    ACTIVE = 'Y',
    CANCELLED = 'N',
    LOCKED = 'L',
    ON_HOLD = 'S',
    OPEN = 'OPEN',
    REFUND = 'T',
    PROCESSING = 'P',
    FAILED = 'F',
    ABANDONED = 'A'
}

export enum CardType {
    UTN = 'U',
    UTN2 = 'T',
    EMAIL = 'Q',
    PERSON = 'C',
    GLOBAL = 'G'
}

export interface Preferences {
    id?: number;
    code: TravelMedia;
    name?: string | { translations: Array<Translation> };
    discountGroups?: string;
    value?: string;
    cardId?: string;
    description?: string;
}

export interface Area {
    id: number | string;
    code: AreaCode | MediumCode;
    name: string | { translations: Array<Translation> };
    description: string | { translations: Array<Translation> };
    subCategories: Area[] | null;
}

export interface Medium {
    id: string;
    code: string;
    name: TranslationContainer;
    description: TranslationContainer;
    products: Product[] | null;
    value: string;
}

export interface TranslationContainer {
    translations: Translation[];
}

export interface Translation {
    lang: string;
    translation: string;
}

export interface ProductArea {
    id: number | string;
    code?: string;
    name: string | { translations: Array<Translation> };
    description: string | { translations: Array<Translation> };
    startZoneId?: number;
    endZoneId?: number;
}

export interface PeriodType {
    id: string;
    code: string;
    name: string | { translations: Array<Translation> };
}

export interface UserProfile {
    id: number;
    name: string;
    description: string;
    price: number;
    minimumAge: number;
    maximumAge: number;
    amount?: number;
}

export interface CompanionProfile {
    dynamicProductParameters: DynamicProductParameter[];
    userProfile: {
        name: Translatable;
        description: Translatable;
        code: string;
    };
    perPersonPrice: Price;
    maxNumberOfPersons: number;
}

export interface SingleTicketGroupTicketTypeLimits {
    total: number;
    perType: {
        [key: string]: number;
    };
}

export interface DynamicProductParameter {
    code: string;
}

export interface ProductGroup {
    code: ProductCode;
    products: CategoryProduct[];
}

export interface BaseFareSubGroup {
    baseFareCode: string;
    baseFareName: string;
    products: Product[];
    maxPrice?: Price;
}

export interface ParentProductGroup {
    name: string;
    code: string;
    groups: ProductGroup[];
    isParentGroup: boolean;
    baseFareSubGroups?: BaseFareSubGroup[];
}

export interface Translation {
    lang: string;
    translation: string;
}

export interface Translatable {
    translations: Translation[];
}

export interface CategoryProduct {
    id: string;
    code: string;
    name: {
        translations: Translation[];
    };
    description: {
        translations: Translation[];
    };
    price?: Price;
    products?: Product[];
    dynamicLength?: boolean;
}

export interface Product {
    id: number | string;
    productId: number | string;
    amount?: number;
    startZoneId?: number;
    endZoneId?: number;
    name?: {
        translations: Translation[];
    };
    description?: {
        translations: Translation[];
    };
    descriptionValue?: string;
    price: Price;
    usageValidityPeriods?: UsageValidityPeriods[];
    userProfiles?: userProfiles;
    activationMethod?: ProductActivationMethod[];
    productParameters?: ProductParameters;
    parametersCode?: string;
    activeTicketDurationInMinutes: number;
    isSingleTicketGroup?: boolean;
    dynamicProductParameters: DynamicProductParameters;
    baseFareProduct?: BaseFareProduct;
    hasDynamicLength?: boolean | null;
    starting?: Date | string;
    ending?: Date | string;
    cardType?: string;
    maxUnits?: number;
}

export interface BaseFareProduct {
    code: string;
    name?: {
        translations: Translation[];
    };
}

export interface DynamicProductParameters {
    [key: string]: number;
}

export interface UsageValidityPeriods {
    id: string;
    code: string;
    name: {
        translations: Translation[];
    };
    description: {
        translations: Translation[];
    };
}

export interface ProductActivationMethod {
    activationTrigger: string;
    name: {
        translations: Translation[];
    };
    description: {
        translations: Translation[];
    };
}

export interface ProductParameters {
    requiredParameters: RequiredParameterType[];
    availableParameters: AvailableParameters;
}

export interface AvailableParameters {
    activations: Activation[];
}

export interface Activation {
    parametersCode: string;
    price: Price;
    activationTrigger: string;
    name: {
        translations: Translation[];
    };
}

export interface userProfiles {
    code: string;
    name: {
        translations: Translation[];
    };
    description: {
        translations: Translation[];
    };
    price: number;
    minAge: number;
    maxAge: number;
    proofRequired: boolean;
}

export interface Price {
    amount: number;
    maxAmount: number | null;
    minAmount: number | null;
    currency: string;
    vatPercent: number;
    vatAmount: number;
    discountedAmount?: number;
    discountAmount?: number;
    discountPerMille?: number;
}

export type ProductsInUrl = Array<string>;

export interface LocalProduct extends Product {
    cardId: string;
}

export interface ProductTravellerType extends Product {
    productTravellerTypeParameterCode?: string;
}

export interface SameTicketsGroup {
    count: number;
    ticket: Ticket;
    totalPrice: number;
}

export interface Ticket {
    id: number;
    productId: number | string;
    name: {
        translations: Translation[];
    };
    price: Price;
    email: string;
    startZoneId?: number;
    endZoneId?: number;
    cardId: string;
    cardType: string;
    valueCodes: ValueCode[];
    description?: string;
    zonesString?: string;
    dynamicProductParameters: DynamicProductParameters;
    userProfiles: {
        companionProfiles: {
            userProfile: {
                nameTranslations: {
                    translations: {
                        [key: string]: string;
                    };
                };
            };
            dynamicProductParameters: DynamicProductParameters[];
        }[];
    }[];
    ticketTypes?: string;
    isSingleTicketGroup?: boolean;
    parametersCode?: string;
    starting?: string;
    productParametersCode?: string;
    units?: number;
}

export type GroupedTicket = Ticket & { amount: number };

export interface Cart {
    shoppingCartId: number;
    parametersCode?: string;
    deletionTimestamp: Date;
    status: CartStatus;
    createdAt: Date;
    updatedAt: Date;
    activatedAt: Date;
    token: string;
    price: Price;
    tickets: Ticket[];
    valueCodes: ValueCode[];
}

export interface CartTicketRequest {
    email?: string;
    productId: string | number;
    startZoneId?: number;
    endZoneId?: number;
    cardId?: string;
    cardType?: string;
    parametersCode?: string;
    dynamicProductParameters?: DynamicProductParameters;
    productParameters?: { [key: string]: number };
    starting?: string | Date;
    units?: number;
    price?: { amount?: number; currency?: string };
}

export interface PrePurchaseCheckResponse {
    checkPassed: boolean;
    code: string;
    message: string;
}

export interface ZoneInterface {
    destination: RoutePoint;
    origin: RoutePoint;
}

export enum ProductCode {
    SINGLE = 'SINGLE',
    SINGLE_TICKET_GROUP = 'SINGLE_TICKET_GROUP',
    SINGLE_TICKET = 'SINGLE_TICKET',
    MULTI_JOURNEY = 'MULTI_JOURNEY',
    MULTI_TRIP = 'MULTI_TRIP',
    PERIOD = 'PERIOD',
    PERIOD_PASS = 'PERIOD_PASS',
    JOINT_TICKET = 'JOINT_TICKET',
    BOOKLET = 'BOOKLET',
    STORED_VALUE = 'STORED_VALUE',
    AMOUNT_VALUE_CODE = 'AMOUNT_VALUE_CODE',
    SALES_DISCOUNT = 'SALES_DISCOUNT',
    OTHER = 'OTHER'
}

export interface CartPaymentMethodsDetails {
    methods: Array<CartPaymentMethod>;
    cards: Array<CartPaymentCard>;
    defaultPosPaymentMethodId: string;
    defaultPaymentCardId: string;
}

export interface CartPaymentMethod {
    posPaymentMethodId: string;
    paymentMethodId: string;
    displayName: string;
    code: string;
    countryCode: string;
    merchantType: string;
    placement: string;
    imageUrl: string;
}

export interface CartPaymentCard {
    id: string;
    holderName: string;
    bin: string;
    lastFourDigits: string;
    expirationMonth: number;
    expirationYear: number;
    type: string;
    imageUrl: string;
    paymentMethodId: string;
}

export interface NextButtonElementContent {
    currency?: string;
    textTerm?: string;
    amount?: number;
}

export interface NextButtonElement {
    content: NextButtonElementContent;
    disabled: boolean;
}

export interface URLData {
    selectedPreferenceId: number;
    selectedAreaId: number;
    selectedProductAreas: string[];
    selectedProductId: number;
    ticketsFromUrl: string[];
}

export interface ValueCode {
    amountValue: number;
    batchName: string;
    discountValue: number;
    serialNumber: string;
}

export interface CategoryProductDetails {
    product_id: number;
    category_code: string;
}

export enum ActivationMode {
    IMMEDIATELY = 'IMMEDIATELY',
    SPECIFIC_DATE = 'SPECIFIC_DATE'
}
